import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";

export interface UploadState {
	state: "uploading" | "error" | "completed";
	progress: number;
	filename?: string;
	error?: string;
}

export interface PendingUpload {
	file: File;
	documentId: string;
	filename: string;
}

export const useGlobalUploadStore = defineStore("global-upload", () => {
	// Track upload states by documentId
	const uploadStates = ref(new Map<string, UploadState>());

	// Track pending uploads by documentId
	const pendingUploads = ref(new Map<string, PendingUpload>());

	function $reset() {
		uploadStates.value = new Map<string, UploadState>();
		pendingUploads.value = new Map<string, PendingUpload>();
	}

	function setUploadState(documentId: string, state: UploadState) {
		uploadStates.value.set(documentId, state);
	}

	function getUploadState(documentId: string | null): UploadState | undefined {
		if (documentId === null) {
			return undefined;
		}

		return uploadStates.value.get(documentId);
	}

	function removeUploadState(documentId: string) {
		uploadStates.value.delete(documentId);
	}

	function setPendingUpload(documentId: string, upload: PendingUpload) {
		pendingUploads.value.set(documentId, upload);
	}

	function getPendingUpload(documentId: string): PendingUpload | undefined {
		return pendingUploads.value.get(documentId);
	}

	function removePendingUpload(documentId: string) {
		pendingUploads.value.delete(documentId);
	}

	function updateUploadProgress(documentId: string, progress: number) {
		const currentState = uploadStates.value.get(documentId);
		if (currentState) {
			setUploadState(documentId, {
				...currentState,
				progress,
			});
		}
	}

	function setUploadError(documentId: string, error: string) {
		const currentState = uploadStates.value.get(documentId);
		if (currentState) {
			setUploadState(documentId, {
				...currentState,
				state: "error",
				error,
			});
		}
	}

	function completeUpload(documentId: string) {
		const currentState = uploadStates.value.get(documentId);
		if (currentState) {
			setUploadState(documentId, {
				...currentState,
				state: "completed",
				progress: 1,
			});
			// Remove completed uploads after a short delay to show completion
			setTimeout(() => {
				removeUploadState(documentId);
			}, 2000);
		}
	}

	const hasActiveUploads = computed(() => {
		return Array.from(uploadStates.value.values()).some(
			(state) => state.state === "uploading",
		);
	});

	const activeUploadCount = computed(() => {
		return Array.from(uploadStates.value.values()).filter(
			(state) => state.state === "uploading",
		).length;
	});

	return {
		uploadStates: readonly(uploadStates),
		pendingUploads: readonly(pendingUploads),
		setUploadState,
		getUploadState,
		removeUploadState,
		setPendingUpload,
		getPendingUpload,
		removePendingUpload,
		updateUploadProgress,
		setUploadError,
		completeUpload,
		hasActiveUploads,
		activeUploadCount,

		$reset,
	};
});
